import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "lucide-react";
import { LANGUAGES } from "@/lib/languages";

interface ArticleFiltersProps {
  search: string;
  onSearchChange: (v: string) => void;
  status: string;
  onStatusChange: (v: string) => void;
  category: string;
  onCategoryChange: (v: string) => void;
  website: string;
  onWebsiteChange: (v: string) => void;
  author: string;
  onAuthorChange: (v: string) => void;
  language: string;
  onLanguageChange: (v: string) => void;
  categories: { id: string; name: string }[];
  websites: { id: string; name: string }[];
  authors: { id: string; full_name: string | null }[];
  perPage: number;
  onPerPageChange: (v: number) => void;
}

const STATUSES = [
  { value: "all", label: "All Statuses" },
  { value: "draft", label: "Draft" },
  { value: "review", label: "In Review" },
  { value: "revision", label: "Revision" },
  { value: "approved", label: "Approved" },
  { value: "scheduled", label: "Scheduled" },
  { value: "published", label: "Published" },
  { value: "archived", label: "Archived" },
];

export default function ArticleFilters({
  search, onSearchChange,
  status, onStatusChange,
  category, onCategoryChange,
  website, onWebsiteChange,
  author, onAuthorChange,
  language, onLanguageChange,
  categories, websites, authors,
  perPage, onPerPageChange,
}: ArticleFiltersProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <div className="relative flex-1 min-w-[200px] max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search title or content..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          className="pl-10 h-9"
        />
      </div>
      <Select value={status} onValueChange={onStatusChange}>
        <SelectTrigger className="w-[150px] h-9"><SelectValue /></SelectTrigger>
        <SelectContent>
          {STATUSES.map((s) => (
            <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={category} onValueChange={onCategoryChange}>
        <SelectTrigger className="w-[150px] h-9"><SelectValue placeholder="Category" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Categories</SelectItem>
          {categories.map((c) => (
            <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={website} onValueChange={onWebsiteChange}>
        <SelectTrigger className="w-[150px] h-9"><SelectValue placeholder="Website" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Websites</SelectItem>
          {websites.map((w) => (
            <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={author} onValueChange={onAuthorChange}>
        <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Author" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Authors</SelectItem>
          {authors.map((a) => (
            <SelectItem key={a.id} value={a.id}>{a.full_name || "Unnamed"}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={language} onValueChange={onLanguageChange}>
        <SelectTrigger className="w-[140px] h-9"><SelectValue placeholder="Language" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Languages</SelectItem>
          {LANGUAGES.map((l) => (
            <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={String(perPage)} onValueChange={(v) => onPerPageChange(Number(v))}>
        <SelectTrigger className="w-[90px] h-9"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="20">20</SelectItem>
          <SelectItem value="50">50</SelectItem>
          <SelectItem value="100">100</SelectItem>
        </SelectContent>
      </Select>
    </div>
  );
}
